<template>
  <BittsModal
    v-if="visible || showDuplicateReportModal"
    title="Duplicate Report"
    primary-button-text="Save a Copy"
    :width="450"
    :loading="loading"
    :disabled="!hasValidName"
    :action-loading="isReportSaving"
    :use-mask-to-close="true"
    @action="saveCopy"
    @closed="onModalClose"
  >
    <p class="text-neutral-text-strong mb-8">
      Please specify a name for a copy of
      <strong>{{ fullReport?.name ?? '' }}</strong>
    </p>
    <BittsInput
      v-model="name"
      name="report-name"
      placeholder="Report Name"
      :status="!hasValidName ? 'danger' : 'default'"
      danger-text="A name for the report is required"
    />
  </BittsModal>
</template>
<script setup lang="ts">
import { BittsInput, BittsModal } from '@crossbeam/bitts';

import { Nullable } from 'vitest';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import { stripUserAddedFromFilters } from '@/composables/reports/userAddedFilterHelpers';
import { ECOSYSTEM } from '@/constants/reports';
import { BASE_ROUTES } from '@/constants/routes';
import { captureException } from '@/errors';
import { ProvidedUserAddedFilterLabels } from '@/injectionKeys/lists';
import { useReportsStore } from '@/stores';
import {
  Report,
  ReportColumn,
  ReportFilterGroup,
  ReportFilterPart,
  ReportPostPayload,
} from '@/types/reports';

const {
  visible = false,
  reportId: reportIdProp,
  noRedirect = false,
} = defineProps<{
  visible?: boolean;
  reportId?: string;
  noRedirect?: boolean;
}>();

const emit = defineEmits<{
  closed: [];
}>();

const loading = ref(true);
const showDuplicateReportModal = ref(false);
const isReportSaving = ref(false);
const fullReport = ref<Report | null>(null);

const route = useRoute();
const router = useRouter();

const reportsStore = useReportsStore();

const userAddedFilterLabels = inject(
  ProvidedUserAddedFilterLabels,
  ref(new Set<string>()),
);

const reportId = computed(() => reportIdProp ?? (route.query.id as string));

/**
 * Fetch the full report to get columns and filters.
 * The list endpoint (v0.4/reports) doesn't include these fields.
 */
async function fetchFullReport() {
  if (!reportId.value) return;

  const fetchedReport = await reportsStore.fetchReportById(reportId.value);
  if (fetchedReport) {
    fullReport.value = fetchedReport;
  }
}

const name = ref<string | undefined>(undefined);
const hasValidName = computed(() => Boolean(name.value?.trim()));
function setName() {
  let nameWithCopy = `${fullReport.value?.name} (Copy)`;
  let reportWithName = reportsStore.getByReportName(nameWithCopy);
  if (!reportWithName) {
    name.value = nameWithCopy;
    return;
  }

  let copyNumber = 1;
  while (reportWithName) {
    nameWithCopy = `${fullReport.value?.name} (Copy ${copyNumber})`;
    reportWithName = reportsStore.getByReportName(nameWithCopy);
    copyNumber++;
  }
  name.value = nameWithCopy;
}

const folderId = computed(() => route.params.folder_id);
const closeRoute = computed(() => {
  if (route.name?.toString().includes('partner_details')) {
    return {
      name: 'partner_details',
      params: { partner_org_id: route.params.partner_org_id },
    };
  } else if (route.name?.toString().includes('lists')) {
    return { name: 'lists' };
  }
  if (!folderId.value) return { name: BASE_ROUTES.REPORTS };
  return {
    name: BASE_ROUTES.REPORT_FOLDER,
    params: { folder_id: folderId.value },
  };
});
function editLink(report: Report) {
  return {
    name: 'edit_report',
    params: { report_id: report.id },
  };
}

type Column = Record<string, Nullable<string | number[] | number | boolean>>;

/** Fields that are added for UI state but should not be sent to the API */
const UI_ONLY_COLUMN_FIELDS = ['compositeColumnId'];

function cleanColumnFields(columns: ReportColumn[]) {
  return columns.map((column) => {
    const validColumn: Column = { ...column };

    const filteredColumn = Object.keys(validColumn).reduce(
      (acc: Column, field) => {
        const { [field]: _, ...rest } = acc;
        // Remove null fields and UI-only fields
        if (
          validColumn[field] === null ||
          UI_ONLY_COLUMN_FIELDS.includes(field)
        ) {
          return rest as ReportColumn;
        }
        return acc;
      },
      validColumn,
    );

    return filteredColumn;
  });
}

type FilterGroup = ReportFilterGroup & {
  report_id?: string;
  id?: number;
  filters?: unknown; // UI state field that should be removed
};

function buildSaveReportPayload(
  report: Report,
  reportName: string,
  userAddedLabels: Set<string>,
): ReportPostPayload {
  const scrubbedFilters = stripUserAddedFromFilters(
    report.filters,
    userAddedLabels,
  );
  const payload: ReportPostPayload = {
    name: reportName,
    is_account_mapping: report.is_account_mapping,
    consolidated_report_type: report.consolidated_report_type,
    use_advanced_filters: report.use_advanced_filters,
    columns: cleanColumnFields(report.columns) as ReportColumn[],
    partner_standard_populations: report.partner_standard_populations,
    segment_filters: report.segment_filters,
    sort: report.sort,
    filters: scrubbedFilters.map((filter) => {
      const newFilter: FilterGroup = {
        ...filter,
        filter_parts: filter.filter_parts.map((filterPart) => {
          const output: ReportFilterPart & {
            id?: number;
            report_population_id?: number;
            combinator?: string; // UI state field
          } = { ...filterPart };
          delete output.id;
          delete output.report_population_id;
          delete output.combinator;
          return output;
        }),
      };
      delete newFilter.report_id;
      delete newFilter.id;
      delete newFilter.filters;
      return newFilter;
    }),
  };
  const isAllPartnersType = payload.consolidated_report_type === ECOSYSTEM;
  if (report.tag_id) payload.tag_id = report.tag_id;
  if (report.partner_segment_filters) {
    payload.partner_segment_filters = report.partner_segment_filters;
  } else {
    if (!isAllPartnersType)
      payload.partner_population_ids = report.partner_population_ids;
    else if (report.partner_impact_scores) {
      payload.partner_impact_scores = report.partner_impact_scores;
    }
    if (report.partner_segments) {
      payload.partner_segments = report.partner_segments;
      if (report.partner_organization_ids) {
        payload.partner_organization_ids = report.partner_organization_ids;
      }
    }
  }
  if (report.our_segment_filters) {
    payload.our_segment_filters = report.our_segment_filters;
  } else if (report.segments) {
    payload.segments = report.segments;
  } else if (report.our_population_ids) {
    payload.our_population_ids = report.our_population_ids;
  }
  if (report.tag_ids) payload.tag_ids = report.tag_ids;
  return payload;
}

onMounted(async () => {
  if (reportIdProp) {
    // Fetch full report to get columns and filters
    await fetchFullReport();
    loading.value = false;
    return;
  }
  showDuplicateReportModal.value = true;
  await reportsStore.refreshReportsStore();
  await fetchFullReport();
  setName();
  loading.value = false;
});

watch(
  () => visible,
  async () => {
    if (visible) {
      await fetchFullReport();
      setName();
    }
  },
);

async function saveCopy() {
  const trimmedName = name.value?.trim();
  if (!trimmedName || !fullReport.value) return;
  const payload = buildSaveReportPayload(
    fullReport.value,
    trimmedName,
    userAddedFilterLabels.value,
  );
  isReportSaving.value = true;
  try {
    const { data: newReport, error } = await crossbeamApi.POST(
      '/v0.5/reports',
      { body: payload },
    );
    if (!newReport) return;
    await reportsStore.upsertReport(newReport);
    await router.push('/'); // force router to update
    await router.push(editLink(newReport));
    emit('closed');
    if (error) {
      console.log(error);
      captureException(error);
    }
    showDuplicateReportModal.value = false;
  } finally {
    isReportSaving.value = false;
  }
}

async function onModalClose() {
  showDuplicateReportModal.value = false;
  if (noRedirect) {
    emit('closed');
  } else {
    await router.push(closeRoute.value);
  }
}
</script>
